import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TrendingUp, AlertTriangle, ShieldOff, RefreshCw, Ban, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin alert panel — surfaces trending bad patterns from recently
 * rejected catchphrases over the last 7 days. Two signals:
 *
 *   1. PHRASES — identical strings rejected by 2+ users (or 2+ times
 *      across the same user) → likely a coordinated bad-faith pattern.
 *   2. TOKENS  — individual words appearing across distinct rejected
 *      phrases. Admins can one-click "Block" any of them, which adds
 *      the word to a dynamic blocklist that auto-rejects all future
 *      submissions containing it.
 *
 * Below the trend panel we also expose the current dynamic blocklist
 * with a per-row remove button so a wrong call is reversible.
 */
export default function AdminCatchphraseTrends({ onPatternBlocked }) {
  const [trends, setTrends] = useState(null);
  const [blocklist, setBlocklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyToken, setBusyToken] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, b] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/catchphrases/trends`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/catchphrases/blocklist`, { withCredentials: true }),
      ]);
      setTrends(t.data);
      setBlocklist(b.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load trends");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const blockWord = async (word, reason) => {
    setBusyToken(word);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/catchphrases/blocklist`,
        { word, reason: reason || null },
        { withCredentials: true },
      );
      toast.success(`"${word}" added to blocklist`);
      await load();
      onPatternBlocked?.(word);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Block failed");
    } finally {
      setBusyToken(null);
    }
  };

  const unblockWord = async (word) => {
    setBusyToken(word);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/catchphrases/blocklist/${encodeURIComponent(word)}`,
        { withCredentials: true },
      );
      toast.success(`"${word}" removed from blocklist`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unblock failed");
    } finally {
      setBusyToken(null);
    }
  };

  const phrases = trends?.phrases || [];
  const tokens = trends?.tokens || [];
  const hasAnything = phrases.length > 0 || tokens.length > 0 || blocklist.length > 0;

  return (
    <section
      className="border border-white/10 bg-black/40 backdrop-blur-xl"
      data-testid="admin-catchphrase-trends"
    >
      <div className="p-4 border-b border-white/10 flex items-center gap-3">
        <TrendingUp className="w-4 h-4 text-[#FF3B30]" />
        <div className="flex-1">
          <div className="label-kicker">Last {trends?.window_days || 7} days</div>
          <div className="font-heading text-xl uppercase">Trending Bad Patterns</div>
        </div>
        {phrases.length + tokens.length > 0 && (
          <span
            className="px-2 py-0.5 text-[10px] tracking-[0.25em] uppercase border border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/10 flex items-center gap-1"
            data-testid="admin-trends-alert-badge"
          >
            <AlertTriangle className="w-3 h-3" /> Alert
          </span>
        )}
        <button
          onClick={load}
          disabled={loading}
          data-testid="admin-trends-refresh"
          className="p-1.5 border border-white/10 hover:border-white/30 transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {loading && !hasAnything ? (
        <div className="p-8 text-center text-white/40 text-[11px] tracking-[0.3em] uppercase">
          Scanning recent rejections…
        </div>
      ) : !hasAnything ? (
        <div className="p-8 text-center text-white/40 text-sm" data-testid="admin-trends-empty">
          No repeating bad patterns. The queue is clean.
        </div>
      ) : (
        <div className="divide-y divide-white/5">
          {/* Phrase repeats */}
          {phrases.length > 0 && (
            <div className="p-4" data-testid="admin-trends-phrases">
              <div className="label-kicker mb-2 text-[#FF3B30]">
                Repeated phrases ({phrases.length})
              </div>
              <ul className="space-y-2">
                {phrases.map((p) => (
                  <li
                    key={p.text}
                    className="flex items-center gap-3 text-sm"
                    data-testid={`admin-trends-phrase-${p.text.slice(0, 20)}`}
                  >
                    <span className="font-heading text-base text-white truncate flex-1">
                      &quot;{p.text}&quot;
                    </span>
                    <span className="font-mono text-[10px] tracking-wider uppercase text-white/50 whitespace-nowrap">
                      {p.count}× / {p.distinct_users} user{p.distinct_users !== 1 ? "s" : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Token repeats — actionable */}
          {tokens.length > 0 && (
            <div className="p-4" data-testid="admin-trends-tokens">
              <div className="label-kicker mb-2 text-[#FFCC00]">
                Recurring tokens ({tokens.length})
              </div>
              <ul className="space-y-2">
                {tokens.map((t) => {
                  const busy = busyToken === t.token;
                  return (
                    <li
                      key={t.token}
                      className="flex items-start gap-3"
                      data-testid={`admin-trends-token-${t.token}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <code className="font-mono text-sm text-[#FFCC00]">
                            {t.token}
                          </code>
                          <span className="font-mono text-[10px] tracking-wider uppercase text-white/40">
                            {t.count}× across distinct rejections
                          </span>
                        </div>
                        {t.examples?.length > 0 && (
                          <div className="text-[11px] text-white/40 mt-1 truncate">
                            e.g. &quot;{t.examples[0]}&quot;
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => blockWord(t.token, `auto: trended in last ${trends?.window_days}d`)}
                        disabled={busy}
                        data-testid={`admin-trends-block-${t.token}`}
                        className={`flex-shrink-0 px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase border-2 flex items-center gap-1.5 transition-colors ${
                          busy
                            ? "border-white/10 text-white/30 cursor-not-allowed"
                            : "border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"
                        }`}
                      >
                        <Ban className="w-3 h-3" /> Block
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Active dynamic blocklist */}
          {blocklist.length > 0 && (
            <div className="p-4" data-testid="admin-trends-blocklist">
              <div className="label-kicker mb-2 flex items-center gap-2">
                <ShieldOff className="w-3 h-3" /> Active blocklist ({blocklist.length})
              </div>
              <ul className="flex flex-wrap gap-2">
                {blocklist.map((b) => {
                  const busy = busyToken === b.word;
                  return (
                    <li
                      key={b.word}
                      className="flex items-center gap-1.5 border border-white/10 px-2 py-1 text-[11px] font-mono"
                      data-testid={`admin-trends-blocklist-${b.word}`}
                      title={b.reason || `Added by ${b.added_by_name || b.added_by}`}
                    >
                      <span className="text-white/80">{b.word}</span>
                      <button
                        onClick={() => unblockWord(b.word)}
                        disabled={busy}
                        data-testid={`admin-trends-unblock-${b.word}`}
                        className={`ml-1 ${busy ? "text-white/20" : "text-white/40 hover:text-[#FF3B30]"}`}
                        title="Remove from blocklist"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
